import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { machineSchema, type Machine, type MachineFormData } from '../../types/machine';
import { machineService } from '../../services/machineService';
import { machineTypeService } from '../../services/machineTypeService';
import type { MachineType } from '../../types/machineType';
import { useWeightUnit } from '../../hooks/useWeightUnit';
import { UnitToggle } from '../../components/common/UnitToggle';
import { WeightInput } from '../../components/common/WeightInput';
import { useToast } from '../../components/common/Toast';
import { getErrorMessage } from '../../utils/error';
import { useLanguage } from '../../features/language/useLanguage';

interface MachineFormProps {
  initialData?: Partial<Machine>;
  initialTypeData?: Partial<MachineType>;
  onSubmit?: (data: MachineFormData) => Promise<void>;
  onCancel: () => void;
  readOnly?: boolean;
  onTypeCreated?: (item: {
    id: string;
    name: string;
    machineId: string;
    isActive: boolean;
    minimumInputKg?: number;
    maximumInputKg?: number;
  }) => void;
  onTypeSaved?: () => void;
  forceMode?: 'machine' | 'type';
}

export const MachineForm: React.FC<MachineFormProps> = ({
  initialData,
  initialTypeData,
  onSubmit,
  onCancel,
  readOnly = false,
  onTypeCreated,
  onTypeSaved,
  forceMode,
}) => {
  const { t } = useLanguage();
  const [mode, setMode] = React.useState<'machine' | 'type'>(forceMode || 'machine');
  const [machines, setMachines] = React.useState<Machine[]>([]);
  const [selectedMachineId, setSelectedMachineId] = React.useState(initialTypeData?.machineId || '');
  const [typeName, setTypeName] = React.useState(
    forceMode === 'type' ? (initialTypeData?.name || '') : ''
  );
  const [typeIsActive, setTypeIsActive] = React.useState(initialTypeData?.isActive ?? true);
  const [minInput, setMinInput] = React.useState<number | undefined>(initialTypeData?.minimumInputKg);
  const [maxInput, setMaxInput] = React.useState<number | undefined>(initialTypeData?.maximumInputKg);
  const { unit, setUnit } = useWeightUnit('kg');
  const [submittingType, setSubmittingType] = React.useState(false);
  const [typeError, setTypeError] = React.useState<string | null>(null);
  const { showToast } = useToast();

  React.useEffect(() => {
    let mounted = true;
    machineService.getAll().then(items => {
      if (mounted) {
        setMachines(items);
      }
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  React.useEffect(() => {
    if (machines.length > 0 && !selectedMachineId) {
      setSelectedMachineId(machines[0].id);
    }
  }, [machines, selectedMachineId]);

  const initialTypeId = initialTypeData?.id;
  const initialTypeMachineId = initialTypeData?.machineId;
  const initialTypeName = initialTypeData?.name;
  const initialTypeIsActive = initialTypeData?.isActive;
  const initialTypeMinimumInputKg = initialTypeData?.minimumInputKg;
  const initialTypeMaximumInputKg = initialTypeData?.maximumInputKg;
  const isInputValid = React.useMemo(() => {
    if (minInput !== undefined && maxInput !== undefined && minInput > maxInput) {
      return false;
    }
    return true;
  }, [minInput, maxInput]);

  React.useEffect(() => {
    if (initialTypeId) {
      setSelectedMachineId(initialTypeMachineId || '');
      setTypeName(initialTypeName || '');
      setTypeIsActive(initialTypeIsActive ?? true);
      setMinInput(initialTypeMinimumInputKg);
      setMaxInput(initialTypeMaximumInputKg);
      return;
    }
    if (forceMode === 'type') {
      setSelectedMachineId('');
      setTypeName('');
      setTypeIsActive(true);
      setMinInput(undefined);
      setMaxInput(undefined);
    }
  }, [
    forceMode,
    initialTypeId,
    initialTypeMachineId,
    initialTypeName,
    initialTypeIsActive,
    initialTypeMinimumInputKg,
    initialTypeMaximumInputKg,
  ]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<MachineFormData>({
    resolver: zodResolver(machineSchema),
    defaultValues: {
      name: forceMode !== 'type' ? initialData?.name || '' : '',
      isActive: initialData?.isActive ?? true,
      notes: initialData?.notes || '',
    },
  });

  const handleFormSubmit = async (data: MachineFormData) => {
    if (mode === 'machine') {
      if (onSubmit) {
        await onSubmit(data);
      }
      return;
    }
    // type creation handled separately (guarded by separate button below as well)
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div className="space-y-4">
        {!forceMode && (
          <div className="flex gap-2">
            <button
              type="button"
              data-test-id="machine-form-mode-machine"
              onClick={() => setMode('machine')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md ${
                mode === 'machine'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('production.machineForm.addMachine')}
            </button>
            <button
              type="button"
              data-test-id="machine-form-mode-type"
              onClick={() => setMode('type')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md ${
                mode === 'type'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('production.machineForm.addType')}
            </button>
          </div>
        )}

        {/* Machine Selector (only for Type Mode) */}
        {mode === 'type' && (
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('production.machineForm.machine')}</label>
            <select
              data-test-id="machine-type-machine"
              value={selectedMachineId}
              onChange={(e) => setSelectedMachineId(e.target.value)}
              disabled={readOnly}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 disabled:bg-gray-50 disabled:text-gray-500"
            >
              {machines.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            {mode === 'type' ? t('production.machineForm.typeName') : t('forms.customer.name')}
          </label>

          {mode === 'machine' ? (
            <input
              data-test-id="machine-form-name"
              {...register('name')}
              disabled={readOnly}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 disabled:bg-gray-50 disabled:text-gray-500"
            />
          ) : (
            <input
              data-test-id="machine-type-name"
              value={typeName}
              onChange={(e) => setTypeName(e.target.value)}
              disabled={readOnly}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 disabled:bg-gray-50 disabled:text-gray-500"
            />
          )}

          {mode === 'machine' && errors.name && (
            <p className="text-red-600 text-sm">{errors.name.message}</p>
          )}
        </div>
        {/* Type Mode Fields */}
        {mode === 'type' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('forms.common.status')}</label>
              <select
                data-test-id="machine-type-status"
                value={typeIsActive ? 'active' : 'inactive'}
                onChange={(e) => setTypeIsActive(e.target.value === 'active')}
                disabled={readOnly}
                className="mt-1 block w-full rounded-md border border-gray-300 p-2 disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="active">{t('forms.common.statusActive')}</option>
                <option value="inactive">{t('forms.common.statusInactive')}</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-gray-700">{t('production.machineForm.minimumInput')}</label>
                  <UnitToggle unit={unit} onToggle={setUnit} />
                </div>
                <WeightInput
                  value={minInput}
                  onChange={(val) => {
                    setMinInput(val);
                    if (typeError) setTypeError(null);
                  }}
                  unit={unit}
                  data-test-id="machine-type-min-input"
                  className={`mt-1 block w-full rounded-md border p-2 disabled:bg-gray-50 disabled:text-gray-500 ${
                    typeError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                  }`}
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-gray-700">{t('production.machineForm.maximumInput')}</label>
                  <UnitToggle unit={unit} onToggle={setUnit} />
                </div>
                <WeightInput
                  value={maxInput}
                  onChange={(val) => {
                    setMaxInput(val);
                    if (typeError) setTypeError(null);
                  }}
                  unit={unit}
                  data-test-id="machine-type-max-input"
                  className={`mt-1 block w-full rounded-md border p-2 disabled:bg-gray-50 disabled:text-gray-500 ${
                    typeError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                  }`}
                />
              </div>
            </div>
            {typeError && <p className="text-red-600 text-sm">{typeError}</p>}
          </>
        )}

        {/* Internal Notes Textarea */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
            {t('forms.common.internalNotes')}
          </label>
          <textarea
            id="notes"
            data-test-id="machine-form-notes"
            {...register('notes')} // Sesuaikan nama field dengan machineSchema Anda
            disabled={readOnly}
            rows={4}
            placeholder={t('production.machineForm.notesPlaceholder')}
            className="mt-1 block w-full rounded-md border border-gray-300 p-2 focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
          />
          {errors.notes && <p className="text-red-600 text-sm">{errors.notes.message}</p>}
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          data-test-id="machine-form-cancel"
          onClick={onCancel}
          className="px-4 py-2 border rounded-md hover:bg-gray-50"
        >
          {t('auth.common.cancel')}
        </button>
        {!readOnly && mode === 'machine' && (
          <button
            type="submit"
            data-test-id="machine-form-submit"
            disabled={isSubmitting}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {initialData ? t('inventory.modal.actions.saveChanges') : t('production.machineForm.createMachine')}
          </button>
        )}
        {!readOnly && mode === 'type' && (
          <button
            type="button"
            data-test-id="machine-type-submit"
            onClick={async () => {
              const resolvedMachineId = selectedMachineId || initialTypeData?.machineId || '';
              if (!resolvedMachineId || typeName.trim() === '') return;

              if (minInput !== undefined && maxInput !== undefined && minInput > maxInput) {
                setTypeError(t('production.machineForm.errors.minGreater'));
                return;
              }
              setTypeError(null);

              setSubmittingType(true);
              try {
                const payload = {
                  machineId: resolvedMachineId,
                  name: typeName.trim(),
                  isActive: typeIsActive,
                  minimumInputKg: minInput,
                  maximumInputKg: maxInput,
                };
                if (initialTypeData?.id) {
                  await machineTypeService.update(initialTypeData.id, payload);
                  showToast(t('production.machineForm.toast.typeUpdated'), 'success');
                } else {
                  const created: MachineType = await machineTypeService.create(payload);
                  const machineName = machines.find(m => m.id === created.machineId)?.name || '';
                  const label = `${machineName}${machineName && created.name ? ' - ' : ''}${created.name}`;
                  onTypeCreated?.({
                    id: created.id,
                    name: label,
                    machineId: created.machineId,
                    isActive: created.isActive,
                    minimumInputKg: created.minimumInputKg,
                    maximumInputKg: created.maximumInputKg,
                  });
                  showToast(t('production.machineForm.toast.typeCreated'), 'success');
                }
                onTypeSaved?.();
                onCancel();
              } catch (error) {
                showToast(getErrorMessage(error, t('production.machineForm.toast.typeSaveFailed')), 'error');
              } finally {
                setSubmittingType(false);
              }
            }}
            disabled={submittingType || typeName.trim() === '' || !selectedMachineId || !isInputValid}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {submittingType ? t('forms.actions.saving') : (initialTypeData?.id ? t('production.machineForm.saveType') : t('production.machineForm.createType'))}
          </button>
        )}
      </div>
    </form>
  );
};
